import { searchObjects } from './commonApi'
import axiosTemp from './axiosTemp'

// ── Types ───────────────────────────────────────────────────────────────────

export interface BlockBillingMetadata {
  is_enabled?: boolean
  is_prohibited_by_ocg?: boolean
  enabled_on?: string
  enabled_by?: number | string
}

export interface BlockBillingClient {
  obj_id: string | number
  number?: string
  name?: string
  metadata?: unknown
  block_billing_is_enabled?: boolean
  block_billing_is_prohibited_by_ocg?: boolean
}

// ── Metadata parsing ─────────────────────────────────────────────────────────

const parseBoolean = (value: string): boolean =>
  value.toLowerCase() === 'true'

/**
 * Client.metadata may come back as an object or as a Python-style repr
 * string, e.g.
 * "{'block_billing': {'is_enabled': True, 'is_prohibited_by_ocg': False,
 *  'enabled_on': '2026-08-26T10:30:00Z', 'enabled_by': 1}}"
 */
export const parseBlockBillingMetadata = (
  client: BlockBillingClient | null | undefined
): BlockBillingMetadata => {
  const raw = client?.metadata
  if (!raw) return {}

  if (typeof raw === 'object') {
    const record = raw as Record<string, unknown>
    const blockBilling =
      record.block_billing && typeof record.block_billing === 'object'
        ? (record.block_billing as Record<string, unknown>)
        : record
    return {
      is_enabled: Boolean(blockBilling.is_enabled),
      is_prohibited_by_ocg: Boolean(blockBilling.is_prohibited_by_ocg),
      enabled_on:
        typeof blockBilling.enabled_on === 'string'
          ? blockBilling.enabled_on
          : undefined,
      enabled_by: blockBilling.enabled_by as number | string | undefined,
    }
  }

  if (typeof raw !== 'string') return {}

  const metadata: BlockBillingMetadata = {}
  const isEnabled = /['"]is_enabled['"]\s*:\s*(true|false)/i.exec(raw)
  if (isEnabled) metadata.is_enabled = parseBoolean(isEnabled[1])
  const isProhibited = /['"]is_prohibited_by_ocg['"]\s*:\s*(true|false)/i.exec(
    raw
  )
  if (isProhibited) metadata.is_prohibited_by_ocg = parseBoolean(isProhibited[1])
  const enabledOn = /['"]enabled_on['"]\s*:\s*['"]([^'"]*)['"]/.exec(raw)
  if (enabledOn) metadata.enabled_on = enabledOn[1]
  const enabledBy = /['"]enabled_by['"]\s*:\s*([0-9]+)/.exec(raw)
  if (enabledBy) metadata.enabled_by = Number(enabledBy[1])
  return metadata
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const CLIENT_FIELDS =
  'obj_id,name,number,metadata,block_billing_is_enabled,block_billing_is_prohibited_by_ocg'

/**
 * Normalise a raw client object returned by the search API.
 * The backend may omit `metadata`, `block_billing_is_enabled`, and
 * `block_billing_is_prohibited_by_ocg` – we fill in safe defaults so
 * downstream code can rely on a consistent shape.
 */
export const normaliseBlockBillingClient = (
  raw: Record<string, unknown>
): BlockBillingClient => ({
  obj_id: raw.obj_id as string | number,
  number: typeof raw.number === 'string' ? raw.number : undefined,
  name: typeof raw.name === 'string' ? raw.name : undefined,
  metadata: raw.metadata ?? undefined,
  block_billing_is_enabled:
    typeof raw.block_billing_is_enabled === 'boolean'
      ? raw.block_billing_is_enabled
      : undefined,
  block_billing_is_prohibited_by_ocg:
    typeof raw.block_billing_is_prohibited_by_ocg === 'boolean'
      ? raw.block_billing_is_prohibited_by_ocg
      : undefined,
})

export const getClientKey = (client: BlockBillingClient): string =>
  String(client.obj_id)

export const isBlockBillingEnabled = (
  client: BlockBillingClient | null | undefined
): boolean =>
  Boolean(
    client?.block_billing_is_enabled ??
      parseBlockBillingMetadata(client).is_enabled
  )

export const isBlockBillingProhibited = (
  client: BlockBillingClient | null | undefined
): boolean =>
  Boolean(
    client?.block_billing_is_prohibited_by_ocg ??
      parseBlockBillingMetadata(client).is_prohibited_by_ocg
  )

export const getBlockBillingEnabledOn = (
  client: BlockBillingClient | null | undefined
): string | undefined => parseBlockBillingMetadata(client).enabled_on

export const getBlockBillingEnabledBy = (
  client: BlockBillingClient | null | undefined
): string | undefined => {
  const enabledBy = parseBlockBillingMetadata(client).enabled_by
  return enabledBy === undefined ? undefined : String(enabledBy)
}

export const buildClientLabel = (
  client: Pick<BlockBillingClient, 'number' | 'name'>
): string => [client.number, client.name].filter(Boolean).join(' ')

// ── API functions ────────────────────────────────────────────────────────────

const extractClientList = (
  response: unknown
): Array<Record<string, unknown>> => {
  const record = response as { data?: unknown } | null | undefined
  const list = Array.isArray(response)
    ? response
    : Array.isArray(record?.data)
      ? record.data
      : []
  return list as Array<Record<string, unknown>>
}

/**
 * Extract `total` when the backend wraps results as `{ data, total }`.
 * The search API may also return a bare array, in which case there is no
 * total and callers must paginate until a short page is returned.
 */
const extractTotal = (response: unknown): number | undefined => {
  if (Array.isArray(response)) return undefined
  const record = response as { total?: unknown } | null | undefined
  return typeof record?.total === 'number' ? record.total : undefined
}

/** Search all clients for the "Select Clients" dropdown */
export async function searchClientsForBlockBilling(
  keywords = '',
  rows = 40
): Promise<BlockBillingClient[]> {
  const response = await searchObjects(
    'client',
    keywords,
    0,
    rows,
    CLIENT_FIELDS,
    'is_active:true',
    true,
    undefined
  )
  return extractClientList(response).map(normaliseBlockBillingClient)
}

/**
 * Fetch all block-billing enabled clients (filtered per-user on the caller).
 * Pages through the search API until every client is retrieved so results
 * exceeding a single page are not truncated.
 */
export async function fetchEnabledClients(): Promise<BlockBillingClient[]> {
  const PAGE_SIZE = 200
  const MAX_PAGES = 50

  const clients: BlockBillingClient[] = []
  let start = 0

  for (let page = 0; page < MAX_PAGES; page++) {
    const response = await searchObjects(
      'client',
      '',
      start,
      PAGE_SIZE,
      CLIENT_FIELDS,
      'block_billing_is_enabled:true',
      true,
      undefined
    )
    const list = extractClientList(response)
    clients.push(...list.map(normaliseBlockBillingClient))

    const total = extractTotal(response)
    start += list.length
    // Stop on an empty page; with a known total stop when all rows are
    // fetched, otherwise stop once the backend returns a short page.
    if (list.length === 0) break
    if (total !== undefined ? start >= total : list.length < PAGE_SIZE) break
  }

  return clients
}

/** PATCH client metadata to enable/disable block billing */
export async function patchClientBlockBilling(
  client: BlockBillingClient,
  enabled: boolean,
  enabledBy: string | number
): Promise<BlockBillingClient> {
  const current = parseBlockBillingMetadata(client)
  const blockBilling: Record<string, unknown> = {
    is_enabled: enabled,
    is_prohibited_by_ocg: current.is_prohibited_by_ocg ?? false,
  }
  if (enabled) {
    blockBilling.enabled_on = new Date().toISOString()
    blockBilling.enabled_by =
      typeof enabledBy === 'string' && /^[0-9]+$/.test(enabledBy)
        ? Number(enabledBy)
        : enabledBy
  } else {
    if (current.enabled_on !== undefined) blockBilling.enabled_on = current.enabled_on
    if (current.enabled_by !== undefined) blockBilling.enabled_by = current.enabled_by
  }

  const response = await axiosTemp().patch(
    `/api/v1.0/accounts/ACCOUNT_ID/clients/${client.obj_id}/`,
    JSON.stringify({ metadata: { block_billing: blockBilling } })
  )
  return response.data
}
